/**
 * SongSequencer — chains saved Sequencer patterns into a song-level
 * arrangement. Each "block" references a pattern in the PatternStore
 * plus a repeat count; the song plays them in order, wrapping back
 * to the first block when looping is enabled or stopping at the end
 * otherwise.
 *
 * How block transitions work — there's no separate clock here. We
 * subscribe to the underlying Sequencer's listener (which fires on
 * every tick) and detect "cursor wrapped from end-of-pattern back to
 * step 0". On each wrap we decrement the current block's remaining
 * repeats; when it hits zero we splat the next block's pattern into
 * the sequencer via `setSteps()` + `length.set()`.
 *
 * State persists to localStorage between sessions so song
 * arrangements survive a reload. Persistence is best-effort — read
 * failures fall back to an empty song without bothering the user.
 */

import type { Sequencer, PatternStore } from '@ac-slayer/synth-core';

const STORAGE_KEY = 'evil-synth/song-sequencer';
const SCHEMA_VERSION = 1;

export interface SongBlock {
  /** Stable id minted at addBlock time. */
  readonly id: string;
  /** The pattern in PatternStore this block plays. */
  readonly patternId: string;
  /**
   * How many times the pattern cycles before the song advances.
   * Clamped to [1, 32] — past 32 the user is better served by a
   * loop-mode song with a longer body.
   */
  readonly repeats: number;
}

export type LoopMode = 'once' | 'loop';

interface SongState {
  readonly schemaVersion: number;
  readonly blocks: readonly SongBlock[];
  readonly loopMode: LoopMode;
}

export type SongListener = (song: SongSequencer) => void;

let blockIdCounter = 0;
function mintBlockId(): string {
  blockIdCounter++;
  return `block-${Date.now().toString(36)}-${blockIdCounter}`;
}

export class SongSequencer {
  private blocks: SongBlock[] = [];
  private loopMode: LoopMode = 'loop';

  /** Block whose pattern is currently loaded in the sequencer.
   *  -1 means the song is stopped. */
  private currentBlockIndex = -1;
  /** Plays remaining for the current block before advancing.
   *  Refilled from `block.repeats` on each block load. */
  private currentRepeatsRemaining = 0;
  /** Last cursor index we saw in the underlying sequencer. Used to
   *  detect wrap (cur=0 while prev>0) without subscribing to a finer
   *  per-tick callback. */
  private prevStep = -1;
  private isPlaying = false;

  private unsubSeq: (() => void) | null = null;
  private readonly listeners = new Set<SongListener>();

  constructor(
    private readonly seq: Sequencer,
    private readonly patternStore: PatternStore,
  ) {
    this.load();
    // If a referenced pattern was deleted while the song was idle,
    // clean those blocks up next time the user looks at the panel.
    this.pruneOrphanBlocks();
    // PatternStore subscriber — if the user deletes a pattern that's
    // referenced by a block, drop those blocks so the song doesn't
    // try to play missing data.
    patternStore.subscribe(() => {
      const before = this.blocks.length;
      this.pruneOrphanBlocks();
      if (this.blocks.length !== before) {
        this.persist();
        this.emit();
      }
    });
  }

  // ── Block management ────────────────────────────────────────────

  getBlocks(): readonly SongBlock[] {
    return this.blocks;
  }

  getLoopMode(): LoopMode {
    return this.loopMode;
  }

  setLoopMode(mode: LoopMode): void {
    if (this.loopMode === mode) return;
    this.loopMode = mode;
    this.persist();
    this.emit();
  }

  /** Append a block to the end. Returns the new block id so callers
   *  can immediately scroll-into-view / focus the new card. */
  addBlock(patternId: string, repeats = 1): string {
    const block: SongBlock = {
      id: mintBlockId(),
      patternId,
      repeats: clampRepeats(repeats),
    };
    this.blocks = [...this.blocks, block];
    this.persist();
    this.emit();
    return block.id;
  }

  removeBlock(id: string): void {
    const before = this.blocks.length;
    this.blocks = this.blocks.filter((b) => b.id !== id);
    if (this.blocks.length === before) return;
    // If we removed the currently-playing block, jump back to the
    // start. Simpler than trying to figure out where in the song the
    // user "should" be — and stopping mid-block on a delete is the
    // less-surprising behavior anyway.
    if (this.isPlaying && this.currentBlockIndex >= this.blocks.length) {
      this.stop();
    }
    this.persist();
    this.emit();
  }

  updateBlock(id: string, patch: Partial<Pick<SongBlock, 'patternId' | 'repeats'>>): void {
    let changed = false;
    this.blocks = this.blocks.map((b) => {
      if (b.id !== id) return b;
      const next: SongBlock = {
        id: b.id,
        patternId: patch.patternId ?? b.patternId,
        repeats: patch.repeats !== undefined ? clampRepeats(patch.repeats) : b.repeats,
      };
      if (next.patternId !== b.patternId || next.repeats !== b.repeats) {
        changed = true;
      }
      return next;
    });
    if (!changed) return;
    this.persist();
    this.emit();
  }

  /** Move a block from index `from` to index `to`. Out-of-range
   *  indexes are clamped; identical from/to is a no-op. */
  moveBlock(from: number, to: number): void {
    const last = this.blocks.length - 1;
    const f = Math.max(0, Math.min(last, from));
    const t = Math.max(0, Math.min(last, to));
    if (f === t) return;
    const next = [...this.blocks];
    const [moved] = next.splice(f, 1);
    if (!moved) return;
    next.splice(t, 0, moved);
    this.blocks = next;
    this.persist();
    this.emit();
  }

  clear(): void {
    if (this.blocks.length === 0) return;
    this.blocks = [];
    this.stop();
    this.persist();
    this.emit();
  }

  // ── Transport ────────────────────────────────────────────────────

  /** Whether the song is currently driving the sequencer. */
  playing(): boolean {
    return this.isPlaying;
  }

  /** Index of the block currently loaded into the sequencer, or -1
   *  when the song is stopped. UI uses this to highlight the cell. */
  getCurrentBlockIndex(): number {
    return this.currentBlockIndex;
  }

  /** Repeats remaining on the current block before advance. The UI
   *  shows this as "2 / 4" so the user can see progress. */
  getCurrentRepeatsRemaining(): number {
    return this.currentRepeatsRemaining;
  }

  /** Start the song. Loads the first block's pattern, subscribes to
   *  the seq for tick-driven advances, and turns the seq on. No-op
   *  if already playing or there are no blocks. */
  play(): void {
    if (this.isPlaying) return;
    if (this.blocks.length === 0) return;
    this.isPlaying = true;
    this.currentBlockIndex = 0;
    this.prevStep = -1;
    if (!this.loadCurrentBlockPattern()) {
      // Pattern missing on first block — bail out cleanly.
      this.stop();
      return;
    }
    this.unsubSeq = this.seq.subscribe(() => this.onSeqEvent());
    this.seq.enabled.set('on', 'ui');
    this.emit();
  }

  /** Stop and reset. Idempotent. */
  stop(): void {
    if (!this.isPlaying && this.currentBlockIndex < 0) return;
    this.isPlaying = false;
    this.currentBlockIndex = -1;
    this.currentRepeatsRemaining = 0;
    this.prevStep = -1;
    if (this.unsubSeq) {
      this.unsubSeq();
      this.unsubSeq = null;
    }
    if (this.seq.enabled.value === 'on') {
      this.seq.enabled.set('off', 'ui');
    }
    this.emit();
  }

  toggle(): void {
    if (this.isPlaying) this.stop();
    else this.play();
  }

  // ── Subscription ────────────────────────────────────────────────

  subscribe(fn: SongListener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  // ── Internal: tick handling ─────────────────────────────────────

  private onSeqEvent(): void {
    if (!this.isPlaying) return;
    // The seq listener fires on edits AND on each tick. We only care
    // about ticks (cursor change), and within those, only the moment
    // of pattern wrap (cursor went from > 0 back to 0).
    const cur = this.seq.getCurrentStepIndex();
    if (cur === this.prevStep) return;
    const wrapped = cur >= 0 && cur < this.prevStep;
    this.prevStep = cur;
    if (!wrapped) {
      // Just a regular step advance — emit for the playhead UI but
      // don't change song state.
      this.emit();
      return;
    }

    // Pattern wrapped — finished one repeat of the current block.
    this.currentRepeatsRemaining--;
    if (this.currentRepeatsRemaining > 0) {
      // Stay on this block for another go-round. Pattern is already
      // loaded in the sequencer; just emit so the UI updates the
      // remaining-repeats display.
      this.emit();
      return;
    }

    // Advance to the next block.
    const nextIndex = this.currentBlockIndex + 1;
    if (nextIndex >= this.blocks.length) {
      if (this.loopMode === 'loop') {
        this.currentBlockIndex = 0;
      } else {
        this.stop();
        return;
      }
    } else {
      this.currentBlockIndex = nextIndex;
    }

    if (!this.loadCurrentBlockPattern()) {
      // Pattern missing — try to skip to the next available block.
      // If we run out of blocks, stop.
      const advanced = this.skipToNextValidBlock();
      if (!advanced) {
        this.stop();
        return;
      }
    }
    this.emit();
  }

  /** Pull the current block's PatternData out of the store and dial
   *  it into the sequencer. Returns true on success, false if the
   *  pattern is missing. */
  private loadCurrentBlockPattern(): boolean {
    const block = this.blocks[this.currentBlockIndex];
    if (!block) return false;
    const pattern = this.patternStore.get(block.patternId);
    if (!pattern) return false;
    // Splat the steps + length onto the sequencer. Other params
    // (rate, swing) we leave alone — they're per-song settings the
    // user dialed in for the active patch, not per-pattern; changing
    // them mid-song would feel jarring.
    this.seq.setSteps(pattern.steps);
    this.seq.length.set(pattern.length, 'preset');
    this.currentRepeatsRemaining = block.repeats;
    return true;
  }

  /** Walk forward through blocks looking for one whose pattern is
   *  still in the store. Returns true if it landed on one. */
  private skipToNextValidBlock(): boolean {
    const start = this.currentBlockIndex;
    const len = this.blocks.length;
    if (len === 0) return false;
    // Scan up to `len` ahead so we wrap once at most when looping.
    for (let i = 1; i <= len; i++) {
      let idx = start + i;
      if (idx >= len) {
        if (this.loopMode !== 'loop') return false;
        idx -= len;
      }
      this.currentBlockIndex = idx;
      if (this.loadCurrentBlockPattern()) return true;
    }
    return false;
  }

  /** Drop blocks whose patterns no longer exist in the store. */
  private pruneOrphanBlocks(): void {
    this.blocks = this.blocks.filter((b) => this.patternStore.get(b.patternId) !== null);
  }

  // ── Persistence ─────────────────────────────────────────────────

  private persist(): void {
    try {
      const state: SongState = {
        schemaVersion: SCHEMA_VERSION,
        blocks: this.blocks,
        loopMode: this.loopMode,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Quota / SecurityError — best effort, don't crash playback.
    }
  }

  private load(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<SongState>;
      if (parsed.schemaVersion !== SCHEMA_VERSION) return;
      if (Array.isArray(parsed.blocks)) {
        this.blocks = parsed.blocks
          .filter((b): b is SongBlock => isValidBlock(b))
          .map((b) => ({ ...b, repeats: clampRepeats(b.repeats) }));
      }
      if (parsed.loopMode === 'once' || parsed.loopMode === 'loop') {
        this.loopMode = parsed.loopMode;
      }
    } catch {
      // Corrupt state — start fresh. We intentionally don't surface
      // the error: the user gains nothing by being told their saved
      // song was unparseable.
    }
  }

  private emit(): void {
    for (const fn of this.listeners) fn(this);
  }
}

function clampRepeats(n: number): number {
  if (!Number.isFinite(n)) return 1;
  return Math.max(1, Math.min(32, Math.round(n)));
}

function isValidBlock(b: unknown): b is SongBlock {
  if (typeof b !== 'object' || b === null) return false;
  const obj = b as Record<string, unknown>;
  return (
    typeof obj['id'] === 'string' &&
    typeof obj['patternId'] === 'string' &&
    typeof obj['repeats'] === 'number'
  );
}
